import {
  Component,
  signal,
  inject,
  input,
  output,
  DestroyRef,
  computed,
  ChangeDetectionStrategy,
  OnInit,
} from '@angular/core';

import { skip, switchMap, filter, finalize, catchError, EMPTY } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { WalletResponseDto } from 'src/generated-sources/openapi/wallet-service/wallet';
import { GenericTableComponent } from 'src/app/shared/components/generic-table/generic-table.component';
import {
  WALLET_DETAILS_TABLE_CONFIG,
  type TrackingRegistryTableView,
} from './wallet-details-table.config';
import { Navbar } from 'src/app/services/models/enums';
import { MatTooltipModule } from '@angular/material/tooltip';
import { GlobalServiceService } from 'src/app/services/global/global-service.service';
import { WalletDetailsFacade } from './wallet-details.facade';
import { WalletDetailsDialogService } from './wallet-details-dialog.service';
import { WALLET_DETAILS_CONFIG, WALLET_TOOLTIPS } from './wallet-details.constants';
import { PageLoaderService } from 'src/app/services/page-loader.service';
import { isAlert30DaysWallet } from 'src/app/helpers/plan-utils';

@Component({
  selector: 'app-wallet-details',
  imports: [GenericTableComponent, MatTooltipModule],
  providers: [WalletDetailsFacade, WalletDetailsDialogService],
  templateUrl: './wallet-details.component.html',
  styleUrl: './wallet-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class WalletDetailsComponent implements OnInit {
  private readonly dialogService = inject(WalletDetailsDialogService);
  private readonly globalService = inject(GlobalServiceService);
  private readonly pageLoaderService = inject(PageLoaderService);
  private readonly facade = inject(WalletDetailsFacade);
  private readonly destroyRef = inject(DestroyRef);

  readonly wallet = input.required<WalletResponseDto>();
  readonly selectedOptionNavbar = output<Navbar>();

  protected readonly walletState = signal<WalletResponseDto | null>(null);
  protected readonly trackingRegistries = signal<TrackingRegistryTableView[]>([]);
  protected readonly currentPage = signal<number>(WALLET_DETAILS_CONFIG.INITIAL_PAGE);
  protected readonly totalRecords = signal(0);
  protected readonly isLoading = signal(false);
  protected readonly sortField = signal<string | null>(null);
  protected readonly sortDirection = signal<'asc' | 'desc'>('asc');

  protected readonly tableConfig = WALLET_DETAILS_TABLE_CONFIG;
  protected readonly pageSize = WALLET_DETAILS_CONFIG.DEFAULT_PAGE_SIZE;

  protected readonly favoriteTooltip = computed(() =>
    this.walletState()?.favorite ? WALLET_TOOLTIPS.removeFavorite() : WALLET_TOOLTIPS.addFavorite()
  );

  protected readonly isAlert30DaysWallet = computed(() =>
    isAlert30DaysWallet(this.walletState()?.planId)
  );

  protected readonly deleteTooltip = WALLET_TOOLTIPS.deleteWallet();
  protected readonly editTooltip = WALLET_TOOLTIPS.editWallet();

  ngOnInit(): void {
    const currentWallet = this.wallet();
    if (currentWallet?.id) {
      this.walletState.set({ ...currentWallet });
      this.loadTrackingRegistries();
    }

    this.globalService
      .getPersonType$()
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.selectedOptionNavbar.emit(Navbar.carteras));
  }

  protected loadTrackingRegistries(): void {
    const wallet = this.walletState();
    if (!wallet?.id) return;

    this.isLoading.set(true);

    this.facade
      .loadTrackingRegistries(
        wallet.id,
        this.currentPage(),
        this.pageSize,
        this.sortField() || undefined,
        this.sortDirection()
      )
      .pipe(
        catchError(() => EMPTY),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe((response) => {
        this.trackingRegistries.set(response.items);
        this.totalRecords.set(response.total);
      });
  }

  protected onPageChange(newPage: number): void {
    this.currentPage.set(newPage);
    this.loadTrackingRegistries();
  }

  handleDeleteDialog(): void {
    const wallet = this.walletState();
    if (!wallet) return;

    this.dialogService
      .openDeleteConfirmation(wallet.name)
      .pipe(
        filter((result) => result === 'confirm'),
        switchMap(() => {
          this.isLoading.set(true);
          return this.facade.deleteWallet(wallet.id, wallet.name).pipe(
            catchError(() => EMPTY),
            finalize(() => this.isLoading.set(false))
          );
        })
      )
      .subscribe(() => this.selectedOptionNavbar.emit(Navbar.carteras));
  }

  handleEditWallet(): void {
    const wallet = this.walletState();
    if (!wallet?.id) return;

    this.pageLoaderService.setPageLoader(true);

    this.facade
      .findWalletById(wallet.id)
      .pipe(
        catchError(() => EMPTY),
        finalize(() => this.pageLoaderService.setPageLoader(false)),
        switchMap((walletData) => this.dialogService.openEditWalletDialog(walletData))
      )
      .subscribe((result) => {
        if (result?.data) {
          this.walletState.update((current) => {
            return {
              ...current,
              name: result.data.name,
              favorite: result.data.favorite,
            };
          });
        }
      });
  }

  handleToggleFavorite(): void {
    const wallet = this.walletState();
    if (!wallet?.id) return;

    this.isLoading.set(true);

    this.facade
      .toggleFavorite(wallet.id)
      .pipe(
        catchError(() => EMPTY),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe(() => {
        this.walletState.update((current) => ({
          ...current,
          favorite: !current.favorite,
        }));
      });
  }
}
